package com.siriusxm.pia.views.unifiedaggregator.operations

import androidx.compose.runtime.*
import com.siriusxm.pia.components.*
import com.siriusxm.pia.operations.cmcServiceClient
import com.siriusxm.pia.operations.components.LogsViewer
import com.siriusxm.pia.operations.components.MetricsChart
import com.siriusxm.pia.operations.components.MonitorsViewer
import com.siriusxm.pia.operations.dashboard.*
import com.siriusxm.pia.operations.dashboard.Row
import com.siriusxm.pia.views.unifiedaggregator.rememberAggregator
import com.siriusxm.unifiedcontent.cmc.CMCService
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * Operations view for the Unified Aggregator application.
 *
 * Provides operational visibility into the unified-content-aggregator service including:
 * - DataDog monitors showing service health status
 * - Publish delay metrics
 * - Recent logs with error/warning filtering
 *
 * Serves as the reference implementation for the dashboard layout DSL (UCA-766): the page is
 * composed with [Dashboard] / [Row] / [Widget] instead of hand-stacked boxes. The Publish
 * Delay chart sits beside a pair of context [StatCard]s in a single row to demonstrate the
 * 12-column layout, and the widgets reflow to full width independently as the viewport narrows.
 *
 * Uses the CMC service API via the cmc-operations module to fetch operational data.
 */
@Composable
fun ServiceViewContext.Operations() {
    val app = rememberAggregator()

    // Create CMC service client for accessing operational data
    val cmcService: CMCService? = remember {
        try {
            app.context.cmcServiceClient()
        } catch (e: Exception) {
            console.error("Failed to create CMC service client: ${e.message}")
            null
        }
    }

    ServiceContentView({
        title = "Operations"
    }) {
        if (cmcService == null) {
            Box({
                title = "Configuration Error"
            }) {
                Div({
                    style {
                        padding(20.px)
                        textAlign("center")
                    }
                }) {
                    Text("CMC API URL is not configured. Please add cmcApiUrl to the configuration.")
                }
            }
        } else {
            val stage = app.context.configuration.stage

            Dashboard {
                // Monitors row - full width. Shows DataDog monitor status for the service.
                Row {
                    Widget(span = 12, minWidth = 320, title = "Service Monitors", paddedContent = false) {
                        MonitorsViewer {
                            client = cmcService
                            tag = "service:aggregator"
                            showFilter = true
                        }
                    }
                }

                // Metrics + context row. The publish-delay chart (span 8) sits beside two
                // narrow context tiles (span 4). On a narrow viewport the chart keeps the top
                // line and the tiles reflow beneath it, each honoring its own minWidth.
                var publishDelayDatadogUrl by remember { mutableStateOf<String?>(null) }
                Row {
                    Widget(
                        span = 8,
                        minWidth = 480,
                        title = "Publish Delay (p99)",
                        config = {
                            publishDelayDatadogUrl?.let { url ->
                                menu {
                                    subAction {
                                        title = "View in DataDog"
                                        action { kotlinx.browser.window.open(url, "_blank") }
                                    }
                                }
                            }
                        }
                    ) {
                        MetricsChart {
                            client = cmcService
                            yAxisLabel = "Seconds"
                            valueTransform = { it / 1000.0 }
                            onDatadogUrl { publishDelayDatadogUrl = it }
                            query(
                                name = "p99",
                                ddQuery = "p99:app_uni_content_catalog.entity.publish.delay{env:$stage,publish_type:direct} by {entity_type}"
                            )
                        }
                    }
                    Widget(span = 2, minWidth = 160) {
                        StatCard(label = "Environment", value = stage.uppercase())
                    }
                    Widget(span = 2, minWidth = 160) {
                        StatCard(
                            label = "CMC API",
                            value = "Connected",
                            trend = Trend(TrendDirection.Up, positiveIsGood = true)
                        )
                    }
                }

                // Logs row - full width. Shows recent errors and warnings.
                var logsDatadogUrl by remember { mutableStateOf<String?>(null) }
                Row {
                    Widget(
                        span = 12,
                        minWidth = 320,
                        title = "Recent Logs",
                        paddedContent = false,
                        config = {
                            logsDatadogUrl?.let { url ->
                                menu {
                                    subAction {
                                        title = "View in DataDog"
                                        action { kotlinx.browser.window.open(url, "_blank") }
                                    }
                                }
                            }
                        }
                    ) {
                        LogsViewer {
                            client = cmcService
                            query = "service:unified-content-aggregator deploymentid:$stage status:(error OR warn)"
                            limit = 100
                            showFilters = true
                            initialTimeRange = TimeRange(PeriodType.Hours.periodOf(1))
                            onDatadogUrl { logsDatadogUrl = it }
                        }
                    }
                }
            }
        }
    }
}
